import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import DataTablePublications from '../components/ui/DataTablePublications.jsx';
import { useTranslate, useTranslationStatus } from '../hooks/useTranslate';
import TranslateAlert from '../components/TranslateAlert';
import { OptimizedImage } from '../components/ui/optimized-image';

// Mapping des catégories vers leurs noms dans la DB
const RUBRIQUE_NAMES = {
  'bulletin': 'Le Bulletin',
  'valet': 'Le Valet',
  'filiations': 'Les filiations lignagères',
  'vademecum': 'Vade-mecum'
};

// Titres traduits (sauf "Valet" qui reste en FR)
const RUBRIQUE_TITLES = {
  'bulletin': 'Bulletin',
  'valet': 'Valet', // Ne pas traduire
  'filiations': 'Filiations',
  'vademecum': 'Vademecum'
};

// Descriptions en français (seront traduites)
const RUBRIQUE_DESCRIPTIONS = {
  'bulletin': 'Le bulletin officiel des Lignages de Bruxelles',
  'valet': 'Le Valet est le périodique trimestriel de l\'association',
  'filiations': 'Documentation des filiations lignagères',
  'vademecum': 'Guide pratique et aide-mémoire'
};

// Composant pour titre traduit (sauf Valet)
const TranslatedTitle = ({ category, title }) => {
  // Ne pas traduire "Valet"
  if (category === 'valet') {
    return <>{title}</>;
  }
  const { translatedText } = useTranslate(title);
  return <>{translatedText}</>;
};

// Composant pour description traduite
// Pour Valet: on traduit la phrase mais on préserve "Le Valet" au début
const TranslatedDescription = ({ description, category }) => {
  // Pour Valet, traduire seulement la partie après "Le Valet"
  if (category === 'valet') {
    const partToTranslate = 'est le périodique trimestriel de l\'association';
    const { translatedText } = useTranslate(partToTranslate);
    return <>Le Valet {translatedText}</>;
  }
  const { translatedText } = useTranslate(description);
  return <>{translatedText}</>;
};

// Composant pour texte traduit simple
const T = ({ children }) => {
  const { translatedText } = useTranslate(children);
  return <>{translatedText}</>;
};

const PublicationsByCategory = () => {
  const { category } = useParams();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const isTranslating = useTranslationStatus();
  
  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
  const rubriqueCategorie = RUBRIQUE_NAMES[category];
  const title = RUBRIQUE_TITLES[category] || 'Publications';
  const description = RUBRIQUE_DESCRIPTIONS[category] || 'Liste des publications disponibles';

  useEffect(() => {
    fetchPageData();
  }, [category]);

  const fetchPageData = async () => {
    try {
      const slug = `publications-${category}`;
      const response = await fetch(`${API_BASE}/api/site/pages/${slug}`);
      if (response.ok) {
        const data = await response.json();
        setPageData(data);
      }
    } catch (error) {
      console.error('Erreur chargement page:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!rubriqueCategorie) {
    return (
      <div className="min-h-screen bg-gray-50 py-16">
        <TranslateAlert />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 
              className="text-5xl font-bold mb-2"
              style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}
            >
              <T>Catégorie non trouvée</T>
            </h1>
            <p className="text-gray-600">
              <T>La catégorie demandée n'existe pas.</T>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <TranslateAlert />
      
      {/* Header Image */}
      {pageData?.header_image && (
        <div 
          className="relative w-full overflow-hidden"
          style={{ 
            height: '600px',
            marginTop: '-80px',
            paddingTop: '80px'
          }}
        >
          <OptimizedImage 
            src={pageData.header_image.startsWith('/api/files/') || pageData.header_image.startsWith('http') ? (pageData.header_image.startsWith('http') ? pageData.header_image : `${API_BASE}${pageData.header_image}`) : `${API_BASE}/api/files/${pageData.header_image}`}
            alt={pageData.title}
            className="absolute inset-0 w-full h-full object-cover"
            priority={true}
            placeholder="blur"
          />
          <div className="absolute inset-0 bg-black bg-opacity-30"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center" style={{ paddingTop: '100px' }}>
            <h1 
              className="text-5xl font-bold text-center px-4"
              style={{ 
                fontFamily: '"Lora", serif',
                color: 'rgb(196, 150, 96)',
                textShadow: '3px 3px 6px rgba(0,0,0,0.8), -1px -1px 2px rgba(255,255,255,0.3)'
              }}
            >
              <TranslatedTitle category={category} title={title} />
            </h1>
            <p 
              className="text-2xl text-center px-4 mt-3"
              style={{ 
                fontFamily: '"Lora", serif',
                color: 'white',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
              }}
            >
              <TranslatedDescription description={description} category={category} />
            </p>
          </div>
        </div>
      )}

      <div className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Titre si pas d'image header */}
          {!pageData?.header_image && (
            <div className="text-center mb-8">
              <h1 
                className="text-5xl font-bold mb-2"
                style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}
              >
                <TranslatedTitle category={category} title={title} />
              </h1>
              <p 
                className="text-gray-600"
                style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
              >
                <TranslatedDescription description={description} category={category} />
              </p>
            </div>
          )}

          <DataTablePublications 
            rubriqueCategorie={rubriqueCategorie} 
            pageLengthDefault={100}
            showCategoryColumn={false}
          />
        </div>
      </div>
    </div>
  );
};

export default PublicationsByCategory;
